import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Users, UserCheck, Eye, Clock, Music, 
  BarChart3, TrendingUp, Calendar, Download,
  Lock, LogOut, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [storedPassword, setStoredPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        setStoredPassword(password);
        fetchDashboardData(password);
      } else {
        setError('Invalid password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async (pwd) => {
    setLoading(true);
    try {
      const [dashboardRes, usersRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/analytics/admin/dashboard`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            password: pwd,
            start_date: startDate,
            end_date: endDate
          })
        }),
        fetch(`${BACKEND_URL}/api/analytics/admin/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: pwd })
        })
      ]);
      
      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboardData(data);
      }
      
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsersData(data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/admin/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          password: storedPassword,
          start_date: startDate,
          end_date: endDate
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `smartreadgo_analytics_${startDate}_${endDate}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setStoredPassword('');
    setDashboardData(null);
    setUsersData(null);
  };

  useEffect(() => {
    if (isAuthenticated && storedPassword) {
      fetchDashboardData(storedPassword);
    }
  }, [startDate, endDate]);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-white/10 backdrop-blur-lg border-white/20">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">SmartReadGo Admin</h1>
            <p className="text-gray-400 mt-2">Enter password to access analytics</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <Input
              type="password"
              placeholder="Admin Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mb-4 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
            <Button 
              type="submit" 
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Access Dashboard'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-slate-900 text-white p-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">SmartReadGo Analytics</h1>
              <p className="text-gray-400 text-sm">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => fetchDashboardData(storedPassword)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleLogout}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Date Filter */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <span className="font-medium">Date Range:</span>
            </div>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-40"
            />
            <span>to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-40"
            />
            <Button onClick={handleExport} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export Data
            </Button>
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'users', 'content'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && dashboardData && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Unique Devices</p>
                        <p className="text-2xl font-bold">{dashboardData.summary.total_unique_devices || dashboardData.summary.total_unique_users}</p>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                      {dashboardData.summary.total_registered_users} registered, {dashboardData.summary.total_guest_users} guests
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <Eye className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Total Events</p>
                        <p className="text-2xl font-bold">{dashboardData.summary.total_events}</p>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                      {dashboardData.summary.total_sessions} sessions
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Time Spent</p>
                        <p className="text-2xl font-bold">{dashboardData.summary.total_time_spent_minutes} min</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Music className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Audio Plays</p>
                        <p className="text-2xl font-bold">{dashboardData.summary.total_audio_plays}</p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Page Views */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-orange-500" />
                    Page Views by Mode
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(dashboardData.page_views).map(([page, count]) => (
                      <div key={page} className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-800">{count}</p>
                        <p className="text-gray-500 capitalize">{page.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Daily Breakdown */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Daily Activity</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Date</th>
                          <th className="text-right py-2">Events</th>
                          <th className="text-right py-2">Registered Users</th>
                          <th className="text-right py-2">Guest Users</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.daily_breakdown.map((day) => (
                          <tr key={day.date} className="border-b">
                            <td className="py-2">{day.date}</td>
                            <td className="text-right">{day.total_events}</td>
                            <td className="text-right">{day.registered_users}</td>
                            <td className="text-right">{day.guest_users}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && usersData && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <UserCheck className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Registered Users</p>
                        <p className="text-2xl font-bold">{usersData.total_registered}</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Guest Users</p>
                        <p className="text-2xl font-bold">{usersData.total_guests}</p>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">All Users</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Type</th>
                          <th className="text-left py-2">Device ID</th>
                          <th className="text-left py-2">Name</th>
                          <th className="text-left py-2">Email</th>
                          <th className="text-right py-2">Duration (min)</th>
                          <th className="text-right py-2">Events</th>
                          <th className="text-right py-2">Last Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usersData.users.map((user, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="py-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                user.type === 'registered' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {user.type}
                              </span>
                            </td>
                            <td className="py-2 text-gray-500 text-xs">
                              {user.device_id?.substring(0, 15) || '-'}
                            </td>
                            <td className="py-2">{user.name}</td>
                            <td className="py-2 text-gray-500">
                              {user.email === '-' ? '-' : user.email}
                            </td>
                            <td className="text-right font-medium">
                              {user.session_duration_minutes || 0}
                            </td>
                            <td className="text-right">{user.total_events}</td>
                            <td className="text-right text-gray-500 text-xs">
                              {user.last_active ? new Date(user.last_active).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}

            {/* Content Tab */}
            {activeTab === 'content' && dashboardData && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Categories */}
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Categories Viewed</h3>
                    {Object.keys(dashboardData.category_views).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(dashboardData.category_views)
                          .sort((a, b) => b[1] - a[1])
                          .map(([category, count]) => (
                            <div key={category} className="flex items-center justify-between">
                              <span className="text-gray-700">{category}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-orange-500 rounded-full"
                                    style={{ 
                                      width: `${(count / Math.max(...Object.values(dashboardData.category_views))) * 100}%` 
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-medium w-8">{count}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No category data for this period</p>
                    )}
                  </Card>

                  {/* Publishers */}
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-4">News Sources Viewed</h3>
                    {Object.keys(dashboardData.publisher_views).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(dashboardData.publisher_views)
                          .sort((a, b) => b[1] - a[1])
                          .map(([publisher, count]) => (
                            <div key={publisher} className="flex items-center justify-between">
                              <span className="text-gray-700">{publisher}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-blue-500 rounded-full"
                                    style={{ 
                                      width: `${(count / Math.max(...Object.values(dashboardData.publisher_views))) * 100}%` 
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-medium w-8">{count}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No publisher data for this period</p>
                    )}
                  </Card>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
